import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EmptyState, ErrorState, LoadingState, formatDate } from "../components/AdminPageStates";
import {
  CoachDetailData,
  CoachEarningAdjustmentRecord,
  CoachEarningRecord,
  CoachPayoutRecord,
  fetchCoachDetail
} from "../services/coachesAdminService";
import { BookingRecord, PaymentRecord } from "../services/realtimeMappers";
import { ShoutOutRecord } from "../services/types";

function DetailSection({
  title,
  emptyMessage,
  children,
  hasData
}: {
  title: string;
  emptyMessage: string;
  children: ReactNode;
  hasData: boolean;
}) {
  return (
    <section className="detail-section">
      <h2>{title}</h2>
      {hasData ? children : <p className="empty-inline">{emptyMessage}</p>}
    </section>
  );
}

function formatCurrency(value: number | null) {
  return value === null
    ? "Unavailable"
    : new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(value);
}

function bookingTitle(booking: BookingRecord) {
  return booking.status ?? "Booking";
}

function EarningList({ earnings }: { earnings: CoachEarningRecord[] }) {
  return (
    <div className="admin-list compact-list">
      {earnings.map((earning) => (
        <section className="admin-list-row" key={earning.id}>
          <div>
            <p className="row-title">{earning.sourceTitle}</p>
            <p className="row-meta">
              Gross {formatCurrency(earning.grossRevenue)} / Chor fee {formatCurrency(earning.chorFee)} / Coach{" "}
              {formatCurrency(earning.estimatedCoachEarnings)}
            </p>
          </div>
          <p className="row-meta">
            {earning.status ?? "Status unavailable"} / Available {formatDate(earning.availableAt)}
          </p>
        </section>
      ))}
    </div>
  );
}

function EarningAdjustmentList({ adjustments }: { adjustments: CoachEarningAdjustmentRecord[] }) {
  return (
    <div className="admin-list compact-list">
      {adjustments.map((adjustment) => (
        <section className="admin-list-row" key={adjustment.id}>
          <div>
            <p className="row-title">{adjustment.status ?? "Refund adjustment"}</p>
            <p className="row-meta">
              Earning {adjustment.earningId ?? "Unavailable"} / Booking {adjustment.bookingId ?? "Unavailable"} / Refund{" "}
              {adjustment.refundId ?? "Unavailable"}
            </p>
          </div>
          <p className="row-meta">
            Adjustment {formatCurrency(adjustment.adjustmentAmountUSD)} / Remaining {formatCurrency(adjustment.remainingAmountUSD)}
          </p>
        </section>
      ))}
    </div>
  );
}

function PayoutList({ payouts }: { payouts: CoachPayoutRecord[] }) {
  return (
    <div className="admin-list compact-list">
      {payouts.map((payout) => (
        <section className="admin-list-row" key={payout.id}>
          <div>
            <p className="row-title">{payout.status ?? "Payout status unavailable"}</p>
            <p className="row-meta">{formatCurrency(payout.amountUSD)}</p>
          </div>
          <p className="row-meta">{formatDate(payout.updatedAt ?? payout.createdAt)}</p>
        </section>
      ))}
    </div>
  );
}

function BookingList({ bookings }: { bookings: BookingRecord[] }) {
  return (
    <div className="admin-list compact-list">
      {bookings.map((booking) => (
        <section className="admin-list-row" key={booking.id}>
          <div>
            <p className="row-title">{bookingTitle(booking)}</p>
            <p className="row-meta">
              User {booking.userId ?? "Unavailable"} / Coach {booking.coachUserId ?? booking.providerUserId ?? "Unavailable"}
            </p>
          </div>
          <p className="row-meta">{formatDate(booking.updatedAt)}</p>
        </section>
      ))}
    </div>
  );
}

function ShoutOutList({ shoutOuts }: { shoutOuts: ShoutOutRecord[] }) {
  return (
    <div className="admin-list compact-list">
      {shoutOuts.map((shoutOut) => (
        <section className="admin-list-row" key={shoutOut.id}>
          <div>
            <p className="row-title">{shoutOut.topic ?? "Shout-out"}</p>
            <p className="row-meta">{shoutOut.privateNote ?? "No comment available"}</p>
          </div>
          <p className="row-meta">{formatDate(shoutOut.createdAt)}</p>
        </section>
      ))}
    </div>
  );
}

function PaymentIssueList({ paymentIssues }: { paymentIssues: PaymentRecord[] }) {
  return (
    <div className="admin-list compact-list">
      {paymentIssues.map((payment) => (
        <section className="admin-list-row" key={payment.id}>
          <div>
            <p className="row-title">{payment.status ?? "Payment issue"}</p>
            <p className="row-meta">Gross {formatCurrency(payment.grossAmountUSD)}</p>
          </div>
          <p className="row-meta">{formatDate(payment.updatedAt)}</p>
        </section>
      ))}
    </div>
  );
}

export default function CoachDetail() {
  const { coachId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<CoachDetailData | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadDetail() {
      if (!coachId) {
        setError("Missing coachId.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        setDetail(await fetchCoachDetail(coachId));
      } catch {
        setError("Unable to load coach detail. Check Firebase permissions and indexes.");
      } finally {
        setIsLoading(false);
      }
    }

    void loadDetail();
  }, [coachId]);

  if (isLoading) {
    return <LoadingState message="Loading coach detail." />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  if (!detail?.coach) {
    return <EmptyState message="Coach profile was not found." />;
  }

  const { coach } = detail;
  const roles = coach.roles.length > 0 ? coach.roles.join(", ") : coach.role ?? "Unavailable";

  return (
    <>
      <button type="button" className="secondary-button back-button" onClick={() => navigate("/coaches")}>
        Back to coaches
      </button>

      <section className="profile-summary">
        <div>
          <p className="eyebrow">Coach profile</p>
          <h2>{coach.displayName ?? coach.username ?? "Unnamed coach"}</h2>
          <p>{coach.email ?? "Email unavailable"}</p>
        </div>
        <div className="summary-grid">
          <span>Coach ID: {coach.id}</span>
          <span>Role(s): {roles}</span>
          <span>Status: {coach.statusLabel}</span>
          <span>Verification: {coach.verificationStatus ?? "Unavailable"}</span>
          <span>Total bookings: {coach.totalBookings ?? "Unavailable"}</span>
          <span>Payout status: {coach.payoutStatus ?? "Unavailable"}</span>
          <span>Gross revenue: {formatCurrency(coach.grossRevenue)}</span>
          <span>Chor fee: {formatCurrency(coach.chorFee)}</span>
          <span>Estimated earnings: {formatCurrency(coach.estimatedCoachEarnings)}</span>
        </div>
      </section>

      <DetailSection title="Services" emptyMessage="No service fields are available for this coach." hasData={coach.servicesOffered.length > 0}>
        <p className="detail-text">{coach.servicesOffered.join(", ")}</p>
      </DetailSection>

      <DetailSection title="Booking History" emptyMessage="No booking history is available." hasData={detail.bookings.length > 0}>
        <BookingList bookings={detail.bookings} />
      </DetailSection>

      <DetailSection title="Earnings Breakdown" emptyMessage="No earnings breakdown is available." hasData={detail.earnings.length > 0}>
        <EarningList earnings={detail.earnings} />
      </DetailSection>

      <DetailSection title="Refund Adjustments" emptyMessage="No refund adjustments are open for this coach." hasData={detail.earningAdjustments.length > 0}>
        <EarningAdjustmentList adjustments={detail.earningAdjustments} />
      </DetailSection>

      <DetailSection title="Payouts" emptyMessage="No payout records are available." hasData={detail.payouts.length > 0}>
        <PayoutList payouts={detail.payouts} />
      </DetailSection>

      <DetailSection title="Shout-Outs / Reviews" emptyMessage="No shout-outs or reviews are available." hasData={detail.shoutOuts.length > 0}>
        <ShoutOutList shoutOuts={detail.shoutOuts} />
      </DetailSection>

      <DetailSection title="Payment Issues" emptyMessage="No payment issues are available." hasData={detail.paymentIssues.length > 0}>
        <PaymentIssueList paymentIssues={detail.paymentIssues} />
      </DetailSection>

      {detail.unavailableSections.length > 0 ? (
        <section className="detail-section">
          <h2>Unavailable Data</h2>
          {detail.unavailableSections.map((item) => (
            <p className="detail-text" key={item}>
              {item}
            </p>
          ))}
        </section>
      ) : null}
    </>
  );
}
